/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import React from 'react';
import { ModalManager } from '../../../components/ModalManager';
import { Modal } from '../../../atoms/bootstrap/Modal';
import { useI18N } from '../../hoc/withI18N';
import { closeModal } from '../../../../actions/modals';
import { useDispatch, useSelector } from 'react-redux';
import ProductRow from '../../molecules/list/v2/ProductRow';
import { ProductHeader } from '../../molecules/list/v2/ProductHeader';
import { ProductSocialProof } from '../../../components/ProductSocialProof';
import { getAEMResource, getHomepage } from '../../../../selectors/generic';
import { isProductTileDataLoading } from '../../../../selectors/products';
import { Button } from '../../../atoms/bootstrap/Button';
import { navigate } from '../../../../utils/redux';
import { useIsMobile } from '../../../hoc/hooks';

export const AddedToCartModalId = 'added-to-cart-modal';

export const AddedToCartModal = ({ payload }) => {
    const dispatch = useDispatch();
    const translate = useI18N();
    const isMobile = useIsMobile();

    const close = React.useCallback(() => dispatch(closeModal()), [
        dispatch,
        closeModal,
    ]);
    const i18n = useSelector((state) => (state as any).myPuratos_i18n?.i18n);
    const homepage = useSelector(getHomepage);

    const otherCustomersBoughtModel =
        useSelector(
            (state) =>
                Object.keys((state as any).aem?.resources).filter((key) =>
                    key.includes('products-social-proof')
                )[0]
        ) || null;
    const otherCustomersBought = useSelector((state) =>
        getAEMResource(state, otherCustomersBoughtModel)
    );
    const otherCustomersBoughtLoading = useSelector(isProductTileDataLoading);
    const hasOtherCustomersBought = otherCustomersBought && homepage;

    const miniCartModel =
        useSelector(
            (state) =>
                Object.keys((state as any).aem?.resources).filter((key) =>
                    key.includes('mini-cart')
                )[0]
        ) || null;
    const miniCart = useSelector((state) =>
        getAEMResource(state, miniCartModel)
    );

    const { sku, quantity } = payload;

    return (
        <Modal
            size={Modal.sizes.XL}
            title={translate(
                'The product has been successfully added to your cart!'
            )}
            closeModal={close}
            icon={<span className="svg-check-mark-green-circle-xxl" />}
            extraClassName="success"
        >
            <div className={'p-datatable'}>
                <ProductHeader />
                <ProductRow
                    key={sku.materialNumber}
                    data={sku}
                    i18n={i18n}
                    titlesClickable={false}
                    staticQuantity={quantity}
                    isMobile={isMobile}
                    isStatic
                />
            </div>

            <Modal.Body
                className={`well p-modal__well ${
                    hasOtherCustomersBought ? 'p-bg-grey1' : ''
                }`}
            >
                <div
                    className={`p-flex p-justify-space-between ${
                        hasOtherCustomersBought ? 'p-margin-bottom-md' : ''
                    } ${isMobile ? 'p-flex-column-reverse' : ''}`}
                >
                    <Button
                        color={Button.colors.SECONDARY_INVERTED}
                        onClick={close}
                        className={'p-padding-left-md p-padding-right-md'}
                    >
                        {translate('Continue shopping')}
                    </Button>
                    <Button
                        color={Button.colors.PRIMARY}
                        onClick={
                            miniCart?.postCartPageLink
                                ? () => navigate(miniCart.postCartPageLink)
                                : close
                        }
                        className={`p-padding-left-md p-padding-right-md ${
                            isMobile ? 'p-margin-bottom-sm' : ''
                        }`}
                    >
                        {translate('Go to shopping cart')}
                    </Button>
                </div>

                {hasOtherCustomersBought && (
                    <div className={'other-customers-bought'}>
                        <ProductSocialProof
                            resource={otherCustomersBought}
                            homepage={homepage}
                            resourceLoading={otherCustomersBoughtLoading}
                            hideAddToCart
                            isInModal={true}
                        />
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

ModalManager.modals[AddedToCartModalId] = AddedToCartModal;
